import { PartnerType } from 'legacy/lut/classes/models/PartnerType'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useState } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button/Button'
import { NotificationController } from 'core-components/library/popup'
import { Tooltip } from 'core-components/library/tooltip'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { buildQbrDocumentOpenLink } from '../../../../lib/documentsAndLinks'
import { createQbrDocument } from '../../../../lib/documentsAndLinks/api'
import { addCockpitQbrDocument } from '../../../../model'
import { CockpitIconButton } from '../../../components/cockpitIconButton'
import { CockpitSectionHeader } from '../../../components/cockpitSectionHeader'

interface IQbrCreateControlsProps {
  iconOnly?: boolean
  onCreatingChange?: (isCreating: boolean) => void
  partnerId: string
  rtm: PartnerType | null
}

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.documentsAndLinks

export const QbrCreateControls = ({
  partnerId,
  rtm,
  iconOnly = false,
  onCreatingChange,
}: IQbrCreateControlsProps) => {
  const t = useSafetyTranslation()
  const dispatch = useAppDispatch()
  const [isCreating, setIsCreating] = useState(false)

  const onCreate = useCallback(async () => {
    if (!rtm || isCreating) {
      return
    }

    setIsCreating(true)
    onCreatingChange?.(true)

    try {
      const document = await createQbrDocument({ partnerId, rtm })
      dispatch(addCockpitQbrDocument({ partnerId, document }))
      NotificationController.showSuccess({ message: 'QBR document created successfully.' })
      window.open(buildQbrDocumentOpenLink(document.EntryId), '_blank', 'noopener,noreferrer')
    } catch (e) {
      NotificationController.showError({ message: 'Failed to create the QBR document.' })
    } finally {
      setIsCreating(false)
      onCreatingChange?.(false)
    }
  }, [dispatch, rtm, partnerId, isCreating, onCreatingChange])

  if (iconOnly) {
    return (
      <CockpitIconButton
        icon="kbase kbase-add"
        label={t(rtm ? KEYS.quickActions.createQbr : KEYS.createRow.rtmUnavailableTooltip)}
        disabled={!rtm || isCreating}
        onClick={onCreate}
      />
    )
  }

  return (
    <CockpitSectionHeader
      actions={
        rtm ? (
          <Button
            size="sm"
            variant="outlined"
            onClick={onCreate}
            disabled={isCreating}
            loading={isCreating}
          >
            <Translation span text={KEYS.createRow.createButton} />
          </Button>
        ) : (
          <Tooltip
            tipContent={() => <Translation span text={KEYS.createRow.rtmUnavailableTooltip} />}
          >
            <Button size="sm" variant="outlined" disabled>
              <Translation span text={KEYS.createRow.createButton} />
            </Button>
          </Tooltip>
        )
      }
    >
      <Translation span text={KEYS.sectionQbr} />
    </CockpitSectionHeader>
  )
}

QbrCreateControls.displayName = 'QbrCreateControls'
